/**
 * What's New preview, rendered with the tenant's own brand build.
 *
 * `gl-ui` ships one entry per dashboard (`utilities`, `dataoris`, `sonarprint`). All three
 * export the SAME What's New component under a brand-specific alias and the same
 * `WhatsNewProps`, so previewing an announcement the way its tenant's users will see it is a
 * matter of loading the matching entry.
 *
 * Entries are loaded lazily and one at a time: a session that only ever previews water
 * content never downloads the dataoris or sonarprint bundle.
 *
 * @module Shared/brand
 */

import type { IGLIcon } from '@group-link-one/gl-icons';
import { Suspense, lazy, useMemo } from 'react';
import type { ComponentType, JSX } from 'react';
import type { Brand } from './pageCatalog';
import { getBrandForTenant } from './pageCatalog';

/** Props of the shared What's New component — identical across the three entries. */
export interface WhatsNewPreviewItem {
  id: string;
  icon: IGLIcon['variant'];
  title: string;
  body: string;
  color: string;
  order: number;
}

export interface WhatsNewPreviewProps {
  /** Tenant the announcement belongs to; decides which brand build renders it. */
  tenantSlug: string | null | undefined;
  open: boolean;
  onClose?: () => void;
  title: string;
  subtitle?: string;
  welcomeMessage?: string;
  footerMessage?: string;
  items: WhatsNewPreviewItem[];
  actionButton: { text: string; onClick: () => void };
}

type PreviewComponent = ComponentType<Omit<WhatsNewPreviewProps, 'tenantSlug'>>;

/** One importer per brand, shared by the lazy components and by {@link preloadWhatsNewPreview}. */
const IMPORT_BY_BRAND: Record<Brand, () => Promise<{ default: PreviewComponent }>> = {
  utilities: () =>
    import('@group-link-one/gl-ui/utilities').then(m => ({
      default: m.UTWhatsNew as PreviewComponent,
    })),
  dataoris: () =>
    import('@group-link-one/gl-ui/dataoris').then(m => ({
      default: m.DOWhatsNew as PreviewComponent,
    })),
  sonarprint: () =>
    import('@group-link-one/gl-ui/sonarprint').then(m => ({
      default: m.SPWhatsNew as PreviewComponent,
    })),
};

/**
 * The lazy component per brand, created once at module load.
 *
 * Built eagerly (not inside the render) because `lazy()` memoizes on identity: a `lazy()` call
 * in the render body would produce a new component every pass and remount the modal.
 */
const PREVIEW_BY_BRAND: Record<Brand, PreviewComponent> = {
  utilities: lazy(IMPORT_BY_BRAND.utilities),
  dataoris: lazy(IMPORT_BY_BRAND.dataoris),
  sonarprint: lazy(IMPORT_BY_BRAND.sonarprint),
};

/**
 * Warms the brand entry for a tenant, so opening the preview does not have to wait for it.
 *
 * Called on hover and focus of the thing that opens the preview. The module cache does the
 * deduplication, so calling it repeatedly costs nothing after the first time.
 */
export function preloadWhatsNewPreview(tenantSlug: string | null | undefined): void {
  void IMPORT_BY_BRAND[getBrandForTenant(tenantSlug)]().catch(() => undefined);
}

/** Renders the announcement in the tenant's brand build. */
export const WhatsNewPreview = ({
  tenantSlug,
  ...props
}: WhatsNewPreviewProps): JSX.Element | null => {
  const Preview = useMemo(() => PREVIEW_BY_BRAND[getBrandForTenant(tenantSlug)], [tenantSlug]);

  if (!props.open) return null;

  /*
    No fallback on purpose. The preview IS a modal, so anything rendered here lands on top of the
    page the person is still looking at — a full-screen spinner for what is usually a cached
    module read one tick long, which reads as the page breaking rather than as a modal opening.
    Rendering nothing means the modal simply appears, and `preloadWhatsNewPreview` keeps that
    moment short even the first time a brand is used.
  */
  return (
    <Suspense fallback={null}>
      <Preview {...props} />
    </Suspense>
  );
};
